/**
 * java.awt.Graphics2D over CanvasRenderingContext2D, plus the supporting AWT types
 * (Font, FontMetrics, BasicStroke, GradientPaint, RadialGradientPaint, AlphaComposite,
 * BufferedImage). This is what lets the game's 12k lines of AWT render code port ~1:1.
 */
import { Color } from './Color';
import { Rectangle, Point2DFloat } from './Geom';

// ---------------------------------------------------------------------------
// Font + FontMetrics
// ---------------------------------------------------------------------------

export class Font {
  static readonly PLAIN = 0;
  static readonly BOLD = 1;
  static readonly ITALIC = 2;

  readonly css: string;

  constructor(readonly name: string, readonly style: number, readonly size: number) {
    const bold = (style & Font.BOLD) !== 0;
    const italic = (style & Font.ITALIC) !== 0;
    const family = name === 'Arial' ? 'Arial, Helvetica, sans-serif' : `${name}, sans-serif`;
    this.css = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${size}px ${family}`;
  }

  getSize(): number { return this.size; }
  deriveFont(size: number): Font { return new Font(this.name, this.style, size); }
}

// Arial's hhea metrics (units/em = 2048): ascender 1854, descender 434, lineGap 67.
// Using the font's own ratios (rather than the browser's reported box) reproduces the
// original Java layout exactly, which matters because every menu centers text with
// `y = ry + (rh - fm.getHeight())/2 + fm.getAscent()`.
const ARIAL_ASCENT = 1854 / 2048;   // 0.90527
const ARIAL_DESCENT = 434 / 2048;   // 0.21191
const ARIAL_LEADING = 67 / 2048;    // 0.03271

let measureCtx: CanvasRenderingContext2D | null = null;
function getMeasureCtx(): CanvasRenderingContext2D {
  if (!measureCtx) {
    const c = document.createElement('canvas');
    c.width = 8;
    c.height = 8;
    measureCtx = c.getContext('2d')!;
  }
  return measureCtx;
}

export class FontMetrics {
  private readonly ascent: number;
  private readonly descent: number;
  private readonly leading: number;
  private readonly widthCache = new Map<string, number>();

  constructor(readonly font: Font) {
    this.ascent = Math.round(font.size * ARIAL_ASCENT);
    this.descent = Math.round(font.size * ARIAL_DESCENT);
    this.leading = Math.round(font.size * ARIAL_LEADING);
  }

  /** java.awt.FontMetrics.stringWidth — integer pixel width. */
  stringWidth(s: string): number {
    if (s === undefined || s === null) return 0;
    const str = String(s);
    const hit = this.widthCache.get(str);
    if (hit !== undefined) return hit;
    const ctx = getMeasureCtx();
    ctx.font = this.font.css;
    const w = Math.round(ctx.measureText(str).width);
    // Cache is per-FontMetrics (which is itself cached per-Font), so it stays bounded
    // in practice — but guard against unbounded growth from ever-changing score strings.
    if (this.widthCache.size > 4096) this.widthCache.clear();
    this.widthCache.set(str, w);
    return w;
  }

  charWidth(c: string): number { return this.stringWidth(c); }
  getAscent(): number { return this.ascent; }
  getDescent(): number { return this.descent; }
  getLeading(): number { return this.leading; }
  getHeight(): number { return this.ascent + this.descent + this.leading; }
}

const fontMetricsCache = new Map<string, FontMetrics>();
function metricsFor(font: Font): FontMetrics {
  let fm = fontMetricsCache.get(font.css);
  if (!fm) {
    fm = new FontMetrics(font);
    fontMetricsCache.set(font.css, fm);
  }
  return fm;
}

// ---------------------------------------------------------------------------
// Stroke / Paint / Composite
// ---------------------------------------------------------------------------

export class BasicStroke {
  static readonly CAP_BUTT = 0;
  static readonly CAP_ROUND = 1;
  static readonly CAP_SQUARE = 2;
  static readonly JOIN_MITER = 0;
  static readonly JOIN_ROUND = 1;
  static readonly JOIN_BEVEL = 2;

  constructor(
    readonly lineWidth: number = 1,
    readonly cap: number = BasicStroke.CAP_SQUARE,
    readonly join: number = BasicStroke.JOIN_MITER,
    readonly miterLimit: number = 10,
    readonly dash: number[] | null = null,
    readonly dashPhase: number = 0,
  ) {}

  get canvasCap(): CanvasLineCap {
    return this.cap === BasicStroke.CAP_ROUND ? 'round'
      : this.cap === BasicStroke.CAP_BUTT ? 'butt'
      : 'square';
  }

  get canvasJoin(): CanvasLineJoin {
    return this.join === BasicStroke.JOIN_ROUND ? 'round'
      : this.join === BasicStroke.JOIN_BEVEL ? 'bevel'
      : 'miter';
  }
}

const DEFAULT_STROKE = new BasicStroke(1);

export class GradientPaint {
  constructor(
    readonly x1: number, readonly y1: number, readonly c1: Color,
    readonly x2: number, readonly y2: number, readonly c2: Color,
  ) {}

  toCanvas(ctx: CanvasRenderingContext2D): CanvasGradient {
    // Canvas rejects zero-length gradients; nudge so it degrades to a solid instead of throwing.
    const dx = this.x2 - this.x1;
    const dy = this.y2 - this.y1;
    const x2 = dx === 0 && dy === 0 ? this.x1 + 0.01 : this.x2;
    const grad = ctx.createLinearGradient(this.x1, this.y1, x2, this.y2);
    grad.addColorStop(0, this.c1.toCss());
    grad.addColorStop(1, this.c2.toCss());
    return grad;
  }
}

export class RadialGradientPaint {
  constructor(
    readonly center: Point2DFloat,
    readonly radius: number,
    readonly fractions: number[],
    readonly colors: Color[],
  ) {}

  toCanvas(ctx: CanvasRenderingContext2D): CanvasGradient {
    const r = Math.max(0.01, this.radius);
    const grad = ctx.createRadialGradient(this.center.x, this.center.y, 0, this.center.x, this.center.y, r);
    for (let i = 0; i < this.fractions.length && i < this.colors.length; i++) {
      const stop = Math.max(0, Math.min(1, this.fractions[i]));
      grad.addColorStop(stop, this.colors[i].toCss());
    }
    return grad;
  }
}

export type Paint = Color | GradientPaint | RadialGradientPaint;

export class AlphaComposite {
  static readonly CLEAR = 1;
  static readonly SRC_OVER = 3;

  private constructor(readonly rule: number, readonly alpha: number) {}

  static getInstance(rule: number, alpha = 1): AlphaComposite {
    return new AlphaComposite(rule, Math.max(0, Math.min(1, alpha)));
  }

  static readonly Clear = AlphaComposite.getInstance(AlphaComposite.CLEAR, 1);
  static readonly SrcOver = AlphaComposite.getInstance(AlphaComposite.SRC_OVER, 1);
}

const OPAQUE_COMPOSITE = AlphaComposite.SrcOver;

// RenderingHints — canvas antialiases unconditionally, so these are accepted and ignored.
export const RenderingHints = {
  KEY_ANTIALIASING: 'aa',
  VALUE_ANTIALIAS_ON: 'on',
  VALUE_ANTIALIAS_OFF: 'off',
  KEY_TEXT_ANTIALIASING: 'taa',
  VALUE_TEXT_ANTIALIAS_ON: 'on',
  KEY_INTERPOLATION: 'interp',
  VALUE_INTERPOLATION_BILINEAR: 'bilinear',
  KEY_RENDERING: 'rendering',
  VALUE_RENDER_QUALITY: 'quality',
} as const;

export const Transparency = {
  OPAQUE: 1,
  BITMASK: 2,
  TRANSLUCENT: 3,
} as const;

// ---------------------------------------------------------------------------
// BufferedImage + GraphicsConfiguration
// ---------------------------------------------------------------------------

export class BufferedImage {
  readonly canvas: HTMLCanvasElement;

  constructor(width: number, height: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.max(1, Math.round(width));
    this.canvas.height = Math.max(1, Math.round(height));
  }

  getWidth(): number { return this.canvas.width; }
  getHeight(): number { return this.canvas.height; }

  createGraphics(): Graphics2D {
    const ctx = this.canvas.getContext('2d')!;
    return new Graphics2D(ctx);
  }
}

export class GraphicsConfiguration {
  createCompatibleImage(width: number, height: number, _transparency?: number): BufferedImage {
    return new BufferedImage(width, height);
  }
}

const SHARED_GC = new GraphicsConfiguration();

// ---------------------------------------------------------------------------
// Graphics2D
// ---------------------------------------------------------------------------

export class Graphics2D {
  private paint: Paint = Color.white;
  private font: Font = new Font('Arial', Font.PLAIN, 12);
  private stroke: BasicStroke = DEFAULT_STROKE;
  private composite: AlphaComposite = OPAQUE_COMPOSITE;
  private clip: Rectangle | null = null;
  /** 1 while a canvas save() is outstanding for the active clip, else 0. */
  private clipSaved = 0;

  constructor(readonly ctx: CanvasRenderingContext2D) {
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.lineWidth = 1;
    ctx.font = this.font.css;
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#fff';
  }

  // ---- state ----

  setColor(c: Color): void {
    this.paint = c;
    const css = c.toCss();
    this.ctx.fillStyle = css;
    this.ctx.strokeStyle = css;
  }

  getColor(): Color {
    return this.paint instanceof Color ? this.paint : Color.white;
  }

  setPaint(p: Paint): void {
    this.paint = p;
    if (p instanceof Color) {
      const css = p.toCss();
      this.ctx.fillStyle = css;
      this.ctx.strokeStyle = css;
    } else {
      const grad = p.toCanvas(this.ctx);
      this.ctx.fillStyle = grad;
      this.ctx.strokeStyle = grad;
    }
  }

  setFont(f: Font): void {
    this.font = f;
    this.ctx.font = f.css;
  }

  getFont(): Font { return this.font; }

  getFontMetrics(f?: Font): FontMetrics {
    return metricsFor(f ?? this.font);
  }

  setStroke(s: BasicStroke): void {
    this.stroke = s;
    this.ctx.lineWidth = s.lineWidth;
    this.ctx.lineCap = s.canvasCap;
    this.ctx.lineJoin = s.canvasJoin;
    this.ctx.miterLimit = s.miterLimit;
    this.ctx.setLineDash(s.dash ?? []);
    this.ctx.lineDashOffset = s.dashPhase;
  }

  getStroke(): BasicStroke { return this.stroke; }

  setComposite(c: AlphaComposite): void {
    this.composite = c;
    if (c.rule === AlphaComposite.CLEAR) {
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.globalAlpha = 1;
    } else {
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.globalAlpha = c.alpha;
    }
  }

  getComposite(): AlphaComposite { return this.composite; }

  setRenderingHint(_key: string, _value: string): void { /* canvas is always antialiased */ }

  getDeviceConfiguration(): GraphicsConfiguration { return SHARED_GC; }

  // ---- transform ----

  translate(x: number, y: number): void { this.ctx.translate(x, y); }
  scale(sx: number, sy: number): void { this.ctx.scale(sx, sy); }
  rotate(theta: number, cx?: number, cy?: number): void {
    if (cx !== undefined && cy !== undefined) {
      this.ctx.translate(cx, cy);
      this.ctx.rotate(theta);
      this.ctx.translate(-cx, -cy);
    } else {
      this.ctx.rotate(theta);
    }
  }

  // ---- clip ----
  //
  // Java's setClip REPLACES the clip; canvas's clip() only ever intersects. So the only
  // way to widen a clip is ctx.restore(). We keep exactly one save() outstanding for the
  // active clip and re-apply our tracked state afterwards, since restore() also rolls back
  // fillStyle/font/alpha/transform.

  getClip(): Rectangle | null {
    return this.clip ? this.clip.getBounds() : null;
  }

  setClip(r: Rectangle | null): void {
    this.clip = r ? r.getBounds() : null;
    this.applyClip();
  }

  clipRect(x: number, y: number, w: number, h: number): void {
    const next = new Rectangle(x, y, w, h);
    this.clip = this.clip ? this.clip.intersection(next) : next;
    this.applyClip();
  }

  private applyClip(): void {
    const t = this.ctx.getTransform();
    if (this.clipSaved > 0) {
      this.ctx.restore();
      this.clipSaved = 0;
    }
    this.ctx.setTransform(t);
    if (this.clip) {
      this.ctx.save();
      this.clipSaved = 1;
      this.ctx.beginPath();
      this.ctx.rect(this.clip.x, this.clip.y, this.clip.width, this.clip.height);
      this.ctx.clip();
    }
    this.reapplyState();
  }

  /** Re-push tracked state onto the context after a restore() blew it away. */
  private reapplyState(): void {
    this.ctx.textBaseline = 'alphabetic';
    this.ctx.textAlign = 'left';
    this.ctx.font = this.font.css;
    this.setPaint(this.paint);
    this.setStroke(this.stroke);
    this.setComposite(this.composite);
  }

  // ---- shapes ----

  fillRect(x: number, y: number, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    this.ctx.fillRect(x, y, w, h);
  }

  drawRect(x: number, y: number, w: number, h: number): void {
    if (w < 0 || h < 0) return;
    this.ctx.strokeRect(x, y, w, h);
  }

  clearRect(x: number, y: number, w: number, h: number): void {
    this.ctx.clearRect(x, y, w, h);
  }

  fillRoundRect(x: number, y: number, w: number, h: number, aw: number, ah: number): void {
    if (w <= 0 || h <= 0) return;
    this.roundRectPath(x, y, w, h, aw, ah);
    this.ctx.fill();
  }

  drawRoundRect(x: number, y: number, w: number, h: number, aw: number, ah: number): void {
    if (w <= 0 || h <= 0) return;
    this.roundRectPath(x, y, w, h, aw, ah);
    this.ctx.stroke();
  }

  private roundRectPath(x: number, y: number, w: number, h: number, aw: number, ah: number): void {
    // Java's arcWidth/arcHeight are the full diameters of the corner arc.
    let r = Math.min(Math.abs(aw) / 2, Math.abs(ah) / 2);
    r = Math.min(r, w / 2, h / 2);
    const ctx = this.ctx;
    ctx.beginPath();
    if (r <= 0) {
      ctx.rect(x, y, w, h);
      return;
    }
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  fillOval(x: number, y: number, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    this.ovalPath(x, y, w, h);
    this.ctx.fill();
  }

  drawOval(x: number, y: number, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    this.ovalPath(x, y, w, h);
    this.ctx.stroke();
  }

  private ovalPath(x: number, y: number, w: number, h: number): void {
    this.ctx.beginPath();
    this.ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  }

  fillArc(x: number, y: number, w: number, h: number, startAngle: number, arcAngle: number): void {
    if (w <= 0 || h <= 0) return;
    const cx = x + w / 2;
    const cy = y + h / 2;
    // Java angles: degrees, 0 = 3 o'clock, counter-clockwise positive. Canvas is clockwise.
    const s = -startAngle * Math.PI / 180;
    const e = -(startAngle + arcAngle) * Math.PI / 180;
    this.ctx.beginPath();
    this.ctx.moveTo(cx, cy);
    this.ctx.ellipse(cx, cy, w / 2, h / 2, 0, s, e, arcAngle > 0);
    this.ctx.closePath();
    this.ctx.fill();
  }

  drawArc(x: number, y: number, w: number, h: number, startAngle: number, arcAngle: number): void {
    if (w <= 0 || h <= 0) return;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const s = -startAngle * Math.PI / 180;
    const e = -(startAngle + arcAngle) * Math.PI / 180;
    this.ctx.beginPath();
    this.ctx.ellipse(cx, cy, w / 2, h / 2, 0, s, e, arcAngle > 0);
    this.ctx.stroke();
  }

  drawLine(x1: number, y1: number, x2: number, y2: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  fillPolygon(xs: number[], ys: number[], n?: number): void {
    this.polygonPath(xs, ys, n ?? xs.length);
    this.ctx.fill();
  }

  drawPolygon(xs: number[], ys: number[], n?: number): void {
    this.polygonPath(xs, ys, n ?? xs.length);
    this.ctx.stroke();
  }

  private polygonPath(xs: number[], ys: number[], n: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    if (n <= 0) return;
    ctx.moveTo(xs[0], ys[0]);
    for (let i = 1; i < n; i++) ctx.lineTo(xs[i], ys[i]);
    ctx.closePath();
  }

  // ---- text ----

  drawString(text: string, x: number, y: number): void {
    this.ctx.fillText(String(text), x, y);
  }

  // ---- images ----

  drawImage(img: BufferedImage | null, x: number, y: number, _observer?: unknown): boolean {
    if (!img) return false;
    this.ctx.drawImage(img.canvas, x, y);
    return true;
  }

  drawImageScaled(img: BufferedImage | null, x: number, y: number, w: number, h: number): boolean {
    if (!img) return false;
    this.ctx.drawImage(img.canvas, x, y, w, h);
    return true;
  }

  // ---- lifecycle ----

  dispose(): void {
    if (this.clipSaved > 0) {
      const t = this.ctx.getTransform();
      this.ctx.restore();
      this.ctx.setTransform(t);
      this.clipSaved = 0;
    }
  }

  /** Reset to a pristine frame state (called once per frame by the engine). */
  resetFrame(): void {
    if (this.clipSaved > 0) {
      this.ctx.restore();
      this.clipSaved = 0;
    }
    this.clip = null;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.globalAlpha = 1;
    this.ctx.globalCompositeOperation = 'source-over';
    this.composite = OPAQUE_COMPOSITE;
    this.stroke = DEFAULT_STROKE;
    this.setStroke(DEFAULT_STROKE);
    this.setColor(Color.white);
  }
}
